import { SPEC_COPY } from '../data/specCopy';
import { ARCHETYPE_LABEL, SLOT_LABEL, SUBSPEC_LIST, SUBSPECS } from '../data/subspecs';
import { renderRulesScreen } from './rulesScreen';
import { getStage } from '../data/stages';
import {
  GEAR_SLOTS,
  LEGENDARY_PITY,
  RARITIES,
  RULE_SLOTS,
  SLOT_IDS,
  SLOT_MAX_RANK,
  type ArchetypeId,
  type Condition,
  type GearSlot,
  type Rarity,
  type TargetKind,
} from '../model/types';
import { heroPower, resolveHero, xpToNext } from '../sim/build';
import { publishedOdds } from '../sim/drops';
import { canRecycle, enhanceCost, itemScore, previewRecycle, sortBag } from '../sim/gear';
import { stageToFight } from '../sim/progress';
import type { GameController } from './controller';
import { slotLabel } from '../game/drawHero';

export function renderStatus(controller: GameController, root: HTMLElement): void {
  const data = controller.save.data;
  const stage = getStage(stageToFight(data.progress));
  const mode = data.progress.drillStage > 0 ? 'Drill' : data.progress.mode === 'farm' ? 'Farm' : 'Push';
  root.innerHTML = `
    <div class="stat">
      <span class="stat-label">${escapeHtml(data.hero.name)}</span>
      <strong>Lv ${data.hero.level}</strong>
    </div>
    <div class="stat">
      <span class="stat-label">Gold</span>
      <strong>${data.currencies.gold}</strong>
    </div>
    <div class="stat">
      <span class="stat-label">Forge dust</span>
      <strong>${data.currencies.forgeDust}</strong>
    </div>
    <div class="stat">
      <span class="stat-label">${mode}</span>
      <strong>${escapeHtml(stage.name)}</strong>
    </div>
    <p class="save-pill">${escapeHtml(controller.account.mode === 'local' ? (controller.account.notice ? 'This browser' : 'Local save') : controller.account.label)}</p>
    <button type="button" data-action="report">Report a bug / idea</button>
    ${
      controller.account.mode === 'cloud'
        ? controller.account.anonymous
          ? '<button type="button" data-action="sign-in-google">Sign in with Google</button>'
          : '<button type="button" data-action="sign-out">Sign out</button>'
        : ''
    }
  `;
}

export function renderPanel(controller: GameController, root: HTMLElement): void {
  const banner = renderBanner(controller);
  const body =
    controller.tab === 'hero'
      ? renderHero(controller)
      : controller.tab === 'build'
        ? controller.rulesOpen
          ? renderRulesScreen()
          : renderBuild(controller)
        : renderCombat(controller);
  root.innerHTML = banner + body;
}

function renderBanner(controller: GameController): string {
  if (controller.account.readOnly) {
    return `<div class="takeover-banner"><p>Signed in on another device. This session is read-only.</p><button type="button" data-action="play-here">Play here</button></div>`;
  }
  if (controller.account.notice) return `<div class="takeover-banner"><p>${escapeHtml(controller.account.notice)}</p></div>`;
  return '';
}

export function renderModal(controller: GameController, root: HTMLElement): void {
  if (controller.feedbackOpen) {
    root.innerHTML = '';
    return;
  }
  if (controller.account.collision) {
    root.innerHTML = `
      <div class="modal-backdrop">
        <div class="modal-card" role="dialog" aria-labelledby="collision-title">
          <p class="eyebrow">One braid</p>
          <h2 id="collision-title">That Google account already has a save</h2>
          <p>Saves are never merged. Keep the Google braid, or stay a guest on this one.</p>
          <div class="button-row">
            <button type="button" data-action="keep-google">Keep the Google braid</button>
            <button type="button" data-action="stay-guest">Stay a guest</button>
          </div>
        </div>
      </div>`;
    return;
  }
  const summary = controller.modal;
  if (!summary) {
    root.innerHTML = '';
    return;
  }
  const gains = summary.gains;
  const capped = gains.capped
    ? `<p class="modal-note">The echo stopped at the ${Math.round(gains.elapsedMs / 3600000)} hour cap. The rest of the absence is not stored.</p>`
    : '';
  const items =
    summary.keptItems.length > 0
      ? `<ul class="modal-loot">${summary.keptItems
          .slice(0, 6)
          .map((item) => `<li class="rarity-${item.rarity}">${escapeHtml(item.name)} · ${item.rarity}</li>`)
          .join('')}${summary.keptItems.length > 6 ? `<li>+${summary.keptItems.length - 6} more</li>` : ''}</ul>`
      : '';
  root.innerHTML = `
    <div class="modal-backdrop" data-action="dismiss">
      <div class="modal-card" role="dialog" aria-labelledby="away-title">
        <p class="eyebrow">The echo kept fighting</p>
        <h2 id="away-title">While you were away</h2>
        <p class="away-time">${formatDuration(gains.elapsedMs)}</p>
        <p>Full rate for the first 2 hours, then 75%. ${capped ? '' : 'Inside the cap.'}</p>
        ${capped}
        <dl class="gain-grid">
          <div><dt>Gold</dt><dd>+${summary.goldGained}</dd></div>
          <div><dt>Experience</dt><dd>+${summary.xpGained}</dd></div>
          <div><dt>Forge dust</dt><dd>+${summary.dustGained}</dd></div>
          <div><dt>Levels</dt><dd>+${summary.levelsGained}</dd></div>
        </dl>
        ${items}
        <p class="modal-note">${
          gains.farmStageId > 0
            ? `Farmed stage ${gains.farmStageId} only. No new stage was cleared.`
            : 'No farm stage is locked yet, so the echo brought nothing back. Clear a stage three times, or drill one.'
        }${summary.salvaged > 0 ? ` ${summary.salvaged} extra drop${summary.salvaged === 1 ? '' : 's'} became forge dust.` : ''}</p>
        ${
          gains.dropRolls > 0 && summary.keptItems.length === 0 && summary.salvaged === 0
            ? `<p class="modal-note">${gains.dropRolls} drop rolls are recorded. Gear from them is minted on the server later.</p>`
            : ''
        }
        <button type="button" data-action="dismiss">Continue</button>
      </div>
    </div>
  `;
}

function renderCombat(controller: GameController): string {
  const data = controller.save.data;
  const stage = getStage(stageToFight(data.progress));
  const hero = resolveHero({
    level: data.hero.level,
    build: data.hero.build,
    gear: data.gear.equipped,
    rules: data.hero.rules,
  });
  const power = heroPower(hero);
  const mode = data.progress.drillStage > 0 ? 'Drilling' : data.progress.mode === 'farm' ? 'Farming' : 'Pushing';
  const log = (controller.fight?.log ?? controller.lastResult?.log ?? []).slice(-8).join('\n');
  const hold = controller.clearHoldText();
  const retry = hold === 'server busy, tap to retry';
  const result = controller.lastResult
    ? `${controller.lastResult.outcome === 'victory' ? 'Cleared' : 'Held'} in ${controller.lastResult.seconds.toFixed(1)}s · +${controller.lastResult.gold} gold${hold ? ` · ${hold}` : ''}`
    : '';
  return `
    <p class="eyebrow">Zone ${stage.zoneId} · ${escapeHtml(stage.zoneName)}</p>
    <h2>${escapeHtml(stage.name)}</h2>
    <div class="chip-row">
      <span class="chip">${mode}</span>
      ${stage.threat.map((tag) => `<span class="chip">${escapeHtml(tag)}</span>`).join('')}
    </div>
    <p class="power-line">Power ${power} · suggested ${stage.recommendedPower}</p>
    <p id="combat-notice" class="${stage.isWall ? 'wall-banner' : 'notice'}">${escapeHtml(data.progress.notice ?? '')}</p>
    ${
      stage.isWall && data.progress.notice !== stage.wallHint
        ? `<p class="wall-banner">${escapeHtml(stage.wallHint ?? 'A wall stands here.')}</p>`
        : ''
    }
    <p id="combat-result" class="result-line${retry ? ' is-retry' : ''}">${escapeHtml(result)}</p>
    <pre id="fight-log" class="fight-log">${escapeHtml(log || 'The echo is stepping in…')}</pre>
    <div class="button-row">
      <button type="button" data-action="pace" data-speed="1" class="${controller.watchSpeed === 1 ? 'is-on' : ''}">Pace ×1</button>
      <button type="button" data-action="pace" data-speed="2" class="${controller.watchSpeed === 2 ? 'is-on' : ''}">Watch ×2</button>
    </div>
    <p class="fine">Watch pace only changes how fast the fight is drawn. Rewards follow the sim ticks.</p>
    ${
      controller.backend.offlineClaims === 'server'
        ? '<h3>Time away</h3><p class="fine">The server clock runs this when you come back. Leaving the tab ends the session. A sign-in on another device does not create offline time. Clears, spec slots, rank spends, and offline rates are written by the server. This browser can still save your name and rules.</p>'
        : `<h3>Time away</h3>
    <p class="fine">Moves the stand-in server clock and runs the real offline formula. This is not a reward button, and it will not exist once Supabase owns the clock.</p>
    <div class="button-row">
      <button type="button" data-action="preview" data-ms="${30 * 60 * 1000}">30 min</button>
      <button type="button" data-action="preview" data-ms="${2 * 60 * 60 * 1000}">2 hours</button>
      <button type="button" data-action="preview" data-ms="${8 * 60 * 60 * 1000}">8 hours</button>
      <button type="button" data-action="preview" data-ms="${12 * 60 * 60 * 1000}">12 hours</button>
    </div>`
    }
  `;
}

function renderHero(controller: GameController): string {
  const data = controller.save.data;
  const slots = GEAR_SLOTS.map((slot) => renderSlot(controller, slot)).join('');
  const visible = sortBag(
    data.gear.bag.filter((item) => controller.bagFilter === 'all' || item.slot === controller.bagFilter),
    controller.bagSort,
  );
  const bag = visible
    .map((item) => {
      const equipped = data.gear.equipped[item.slot];
      const better = !equipped || itemScore(item) > itemScore(equipped);
      const locked = item.locked === true;
      return `
        <li class="bag-row rarity-${item.rarity}">
          <div>
            <strong>${escapeHtml(item.name)}</strong>
            <span>${item.rarity} · T${item.tier} · +${item.enhance}${better ? ' · stronger' : ''}</span>
          </div>
          <div class="button-row">
            <button type="button" data-action="${locked ? 'unlock' : 'lock'}" data-id="${escapeHtml(item.id)}" aria-pressed="${locked ? 'true' : 'false'}" aria-label="${locked ? 'Unlock' : 'Lock'} ${escapeHtml(item.name)}">${lockIcon(locked)}</button>
            <button type="button" data-action="equip" data-id="${escapeHtml(item.id)}">Equip</button>
            ${locked || !canRecycle(item, data.gear.equipped) ? '' : `<button type="button" data-action="salvage" data-id="${escapeHtml(item.id)}">Salvage</button>`}
          </div>
        </li>`;
    })
    .join('');
  const odds = publishedOdds(stageTier(controller))
    .map((row) => `<li><span class="swatch rarity-${row.rarity}"></span>${labelRarity(row.rarity)} ${row.percent.toFixed(1)}%</li>`)
    .join('');
  const filters = ['all', ...GEAR_SLOTS]
    .map((slot) => {
      const label = slot === 'all' ? 'All' : slotLabel(slot as GearSlot);
      const on = controller.bagFilter === slot ? ' is-on' : '';
      return `<button type="button" data-action="bag-filter" data-slot="${slot}" class="${on.trim()}">${label}</button>`;
    })
    .join('');
  const below = RARITIES.filter((rarity) => rarity !== 'common')
    .map((rarity) => `<option value="${rarity}" ${controller.recycleBelow === rarity ? 'selected' : ''}>${labelRarity(rarity)}</option>`)
    .join('');
  const auto = data.gear.autoRecycleBelow;
  const autoOptions = [`<option value="" ${auto ? '' : 'selected'}>Off</option>`]
    .concat(
      RARITIES.filter((rarity) => rarity !== 'common').map(
        (rarity) => `<option value="${rarity}" ${auto === rarity ? 'selected' : ''}>Below ${labelRarity(rarity)}</option>`,
      ),
    )
    .join('');
  const preview = previewRecycle(data, controller.recycleBelow);
  const sortLabel = controller.bagSort === 'high' ? 'Rarity: high to low' : 'Rarity: low to high';
  return `
    <p class="eyebrow">Visible braid</p>
    <h2>Gear</h2>
    ${renderStageControls(controller)}
    <div class="gear-box">
      <h3>Equipped</h3>
      <p class="fine">Six slots. Tier changes the shape, rarity adds trim and glow.</p>
      <ul class="slot-list">${slots}</ul>
    </div>
    <div class="bag-box">
      <h3>Bag</h3>
      <div class="bag-toolbar">
        <div class="button-row" role="group" aria-label="Slot filter">${filters}</div>
        <button type="button" data-action="bag-sort" aria-pressed="${controller.bagSort === 'high' ? 'true' : 'false'}">${sortLabel}</button>
      </div>
      <div class="recycle-controls">
        <label class="fine">Recycle unlocked below
          <select data-action="recycle-below">${below}</select>
        </label>
        <button type="button" data-action="recycle-bulk" ${preview.count < 1 ? 'disabled' : ''}>Recycle all unlocked below ${labelRarity(controller.recycleBelow)}</button>
        <label class="fine">Auto-recycle new drops
          <select data-action="auto-recycle">${autoOptions}</select>
        </label>
      </div>
      ${bag ? `<ul class="bag-list">${bag}</ul>` : '<p class="fine bag-empty">Nothing in this view. Clears drop gear. Nothing is sold.</p>'}
    </div>
    <p class="pity">Legendary pity ${data.gear.pity.epicPlusSinceLegendary} / ${LEGENDARY_PITY}</p>
    <p class="fine">After ${LEGENDARY_PITY} Epic-or-better drops without a Legendary, the next Epic-or-better drop is a Legendary.</p>
    <details>
      <summary>Drop odds · tier ${stageTier(controller)} table</summary>
      <ul class="odds">${odds}</ul>
    </details>
    <p class="fine">Gold and forge dust are earned from fights and salvage. Recycling pays the same forge dust, and never a locked, equipped, or boss-set piece.</p>
    <details>
      <summary>This browser's save</summary>
      <button type="button" data-action="reset">Reset local save</button>
    </details>
    ${controller.recycleToast ? `<p class="recycle-toast" role="status">${escapeHtml(controller.recycleToast)}</p>` : ''}
  `;
}

function renderStageControls(controller: GameController): string {
  const data = controller.save.data;
  const candidate = controller.drillCandidate();
  return `
    <div class="stage-controls">
      ${data.progress.mode === 'farm' || data.progress.drillStage > 0 ? '<button type="button" data-action="push">Push on</button>' : ''}
      ${candidate ? `<button type="button" data-action="drill">Drill stage ${candidate}</button>` : ''}
      ${data.progress.drillStage > 0 ? '<button type="button" data-action="cancel-drill">Stop drill</button>' : ''}
    </div>`;
}

function lockIcon(locked: boolean): string {
  if (locked) {
    return '<svg class="lock-icon" viewBox="0 0 16 16" aria-hidden="true"><rect x="3.5" y="7" width="9" height="6.5" rx="1"></rect><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"></path></svg>';
  }
  return '<svg class="lock-icon is-open" viewBox="0 0 16 16" aria-hidden="true"><rect x="3.5" y="7" width="9" height="6.5" rx="1"></rect><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0"></path></svg>';
}

function renderSlot(controller: GameController, slot: GearSlot): string {
  const item = controller.save.data.gear.equipped[slot];
  if (!item) {
    return `<li class="slot-row"><div><span class="slot-name">${slotLabel(slot)}</span><strong>Empty</strong></div></li>`;
  }
  const cost = enhanceCost(item);
  const wallet = controller.save.data.currencies;
  const afford = item.enhance < 10 && wallet.gold >= cost.gold && wallet.forgeDust >= cost.forgeDust;
  const stats = [`HP ${item.stats.hp}`, `Atk ${item.stats.attack}`, `Arm ${item.stats.armor}`].filter((line) => !line.endsWith(' 0'));
  return `
    <li class="slot-row rarity-${item.rarity}">
      <div>
        <span class="slot-name">${slotLabel(slot)}</span>
        <strong>${escapeHtml(item.name)}</strong>
        <span>${labelRarity(item.rarity)} · T${item.tier} · +${item.enhance}</span>
        <span class="fine">${stats.join(' · ')}</span>
      </div>
      <div class="button-row">
        <button type="button" data-action="${item.locked ? 'unlock' : 'lock'}" data-id="${escapeHtml(item.id)}" aria-pressed="${item.locked ? 'true' : 'false'}" aria-label="${item.locked ? 'Unlock' : 'Lock'} ${escapeHtml(item.name)}">${lockIcon(item.locked === true)}</button>
        <button type="button" data-action="enhance" data-slot="${slot}" ${afford ? '' : 'disabled'}>
          Enhance ${item.enhance >= 10 ? 'max' : `${cost.gold}g / ${cost.forgeDust} dust`}
        </button>
        <button type="button" data-action="unequip" data-slot="${slot}">Bag</button>
      </div>
    </li>`;
}

function renderBuild(controller: GameController): string {
  const data = controller.save.data;
  const hero = resolveHero({
    level: data.hero.level,
    build: data.hero.build,
    gear: data.gear.equipped,
    rules: data.hero.rules,
  });
  const need = xpToNext(data.hero.level);
  const slots = SLOT_IDS.map((slot) => {
    const specId = data.hero.build[slot];
    const spec = specId ? SUBSPECS[specId] : null;
    const rank = specId ? (data.hero.build.ranks[specId] ?? 0) : 0;
    const cap = SLOT_MAX_RANK[slot];
    const options = (['ironbound', 'veilweaver', 'wildkin', 'shadecaller'] as ArchetypeId[])
      .map((archetype) => {
        const group = SUBSPEC_LIST.filter((candidate) => candidate.archetype === archetype)
          .map((candidate) => {
            const taken = SLOT_IDS.some((other) => other !== slot && data.hero.build[other] === candidate.id);
            return `<option value="${candidate.id}" ${candidate.id === specId ? 'selected' : ''} ${taken ? 'disabled' : ''}>${escapeHtml(candidate.name)}</option>`;
          })
          .join('');
        return `<optgroup label="${ARCHETYPE_LABEL[archetype]}">${group}</optgroup>`;
      })
      .join('');
    const abilities = hero.abilities
      .filter((ability) => ability.specId === specId)
      .map((ability) => `<li>${escapeHtml(ability.name)}</li>`)
      .join('');
    const canSpend = data.hero.unspentPoints > 0 && spec !== null && rank < cap;
    const spendDisabled = canSpend ? '' : 'disabled';
    const copy = spec ? SPEC_COPY[spec.id] : null;
    const spendName = spec ? escapeHtml(spec.name) : 'this sub-spec';
    return `
      <article class="mark-card">
        <p class="eyebrow">${SLOT_LABEL[slot].mark} · ${SLOT_LABEL[slot].weight}</p>
        <label>Sub-spec
          <select data-action="set-spec" data-slot="${slot}">
            ${options}
          </select>
        </label>
        ${
          spec && copy
            ? `<p class="spec-name">${escapeHtml(spec.name)}</p>
               <p class="spec-line">${escapeHtml(copy.line)}</p>
               <details class="spec-more" data-spec="${spec.id}">
                 <summary>More<span class="visually-hidden"> about ${escapeHtml(spec.name)}</span></summary>
                 <p>${escapeHtml(copy.detail)}</p>
               </details>
               <p class="fine">${ARCHETYPE_LABEL[spec.archetype]} · ${spec.secondaryRole ? `${spec.role} / ${spec.secondaryRole}` : spec.role} · rank ${rank}/${cap}</p>`
            : '<p class="fine">Choose a Mark.</p>'
        }
        <div class="spend-row">
          <button type="button" data-action="spend" data-slot="${slot}" data-count="1" aria-label="+1, spend 1 point on ${spendName}" ${spendDisabled}>+1</button>
          <button type="button" data-action="spend" data-slot="${slot}" data-count="5" aria-label="+5, spend 5 points on ${spendName}" ${spendDisabled}>+5</button>
          <button type="button" data-action="spend" data-slot="${slot}" data-count="max" aria-label="Max, spend all points on ${spendName}" ${spendDisabled}>Max</button>
        </div>
        ${abilities ? `<ul class="ability-list">${abilities}</ul>` : ''}
      </article>`;
  }).join('');
  const affinity =
    hero.affinity === 'pure'
      ? `Pure ${hero.heart ? ARCHETYPE_LABEL[hero.heart] : 'braid'}. +10% health and attack. ${hero.resourceName ?? 'Resource'} pool +10%.`
      : hero.affinity === 'triad'
        ? `Triad braid. The Hand ultimate is ready. ${hero.resourceName ?? 'Resource'} pool −5%.`
        : hero.fusionName
          ? `Hybrid braid. ${hero.fusionName}: ${hero.fusionText ?? ''}`
          : 'Hybrid braid.';
  const rules = data.hero.rules
    .map((rule, index) => renderRule(controller, rule, index))
    .join('');
  return `
    <p class="eyebrow">Three strands</p>
    <div class="build-head">
      <h2>Build</h2>
      <button type="button" id="open-rules" data-action="open-rules">Rules</button>
    </div>
    <p class="points">${data.hero.unspentPoints} unspent point${data.hero.unspentPoints === 1 ? '' : 's'} · ${need === 0 ? 'level cap' : `${data.hero.xp}/${need} xp`}</p>
    <dl class="stat-preview">
      <div><dt>Health</dt><dd>${hero.maxHp}</dd></div>
      <div><dt>Attack</dt><dd>${hero.attack}</dd></div>
      <div><dt>Armor</dt><dd>${hero.armor}</dd></div>
      <div><dt>Armor break</dt><dd>${hero.armorBreak}</dd></div>
      <div><dt>Heal</dt><dd>${hero.healPower}</dd></div>
      <div><dt>${escapeHtml(hero.resourceName ?? 'Resource')}</dt><dd>${hero.resourcePool}</dd></div>
    </dl>
    <p class="fine">${escapeHtml(affinity)} Re-braid is free and keeps these three sub-specs.</p>
    <p class="fine">Hold +1 or +5 to spend faster. It speeds up while you hold.</p>
    <h3>Rules</h3>
    <p class="fine">Each tick, the first matching rule fires. ${data.hero.rules.length}/${RULE_SLOTS} slots. More slots are earned later, never sold.</p>
    <ol class="rule-list">${rules}</ol>
    <button type="button" data-action="rule-add" ${data.hero.rules.length >= RULE_SLOTS ? 'disabled' : ''}>Add rule</button>
    <div class="mark-grid">${slots}</div>
    <div class="button-row"><button type="button" data-action="rebraid">Re-braid</button></div>
  `;
}

function renderRule(controller: GameController, rule: { id: string; condition: Condition; abilityId: string; target: TargetKind }, index: number): string {
  const abilities = resolveHero({
    level: controller.save.data.hero.level,
    build: controller.save.data.hero.build,
    gear: controller.save.data.gear.equipped,
    rules: controller.save.data.hero.rules,
  }).abilities;
  const abilityOptions = abilities
    .map((ability) => `<option value="${ability.id}" ${ability.id === rule.abilityId ? 'selected' : ''}>${escapeHtml(ability.name)}</option>`)
    .join('');
  const param =
    rule.condition.kind === 'selfHpBelow'
      ? `<input data-action="rule-param" data-index="${index}" type="number" min="5" max="95" value="${rule.condition.pct}" aria-label="HP percent" />`
      : rule.condition.kind === 'enemyCountAtLeast'
        ? `<input data-action="rule-param" data-index="${index}" type="number" min="1" max="6" value="${rule.condition.count}" aria-label="Enemy count" />`
        : '';
  return `
    <li>
      <div class="rule-line">
        <span>IF</span>
        <select data-action="rule-condition" data-index="${index}">
          ${conditionOptions(rule.condition.kind)}
        </select>
        ${param}
        <span>THEN</span>
        <select data-action="rule-ability" data-index="${index}">${abilityOptions}</select>
        <span>ON</span>
        <select data-action="rule-target" data-index="${index}">
          ${targetOptions(rule.target)}
        </select>
      </div>
      <div class="button-row">
        <button type="button" data-action="rule-up" data-index="${index}" ${index === 0 ? 'disabled' : ''}>Up</button>
        <button type="button" data-action="rule-down" data-index="${index}">Down</button>
        <button type="button" data-action="rule-remove" data-index="${index}">Remove</button>
      </div>
    </li>`;
}

function conditionOptions(current: Condition['kind']): string {
  const options: [Condition['kind'], string][] = [
    ['always', 'Always'],
    ['selfHpBelow', 'Self HP below'],
    ['enemyCountAtLeast', 'Enemy count at least'],
    ['targetIsEliteOrBoss', 'Target is elite or boss'],
    ['targetHasArmor', 'Target has armor'],
  ];
  return options.map(([kind, label]) => `<option value="${kind}" ${kind === current ? 'selected' : ''}>${label}</option>`).join('');
}

function targetOptions(current: TargetKind): string {
  const options: [TargetKind, string][] = [
    ['self', 'Self'],
    ['lowestHp', 'Lowest HP'],
    ['highestHp', 'Highest HP'],
    ['boss', 'Boss'],
  ];
  return options.map(([kind, label]) => `<option value="${kind}" ${kind === current ? 'selected' : ''}>${label}</option>`).join('');
}

function stageTier(controller: GameController): 1 | 2 | 3 {
  const id = stageToFight(controller.save.data.progress);
  return getStage(id).tier;
}

function labelRarity(rarity: Rarity): string {
  return rarity.charAt(0).toUpperCase() + rarity.slice(1);
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    if (char === '&') return '&amp;';
    if (char === '<') return '&lt;';
    if (char === '>') return '&gt;';
    if (char === '"') return '&quot;';
    return '&#39;';
  });
}
